import { supabase } from './supabase';
import type { Database } from './supabase';

// Type helpers
type Client = Database['public']['Tables']['clients']['Row'];
type Project = Database['public']['Tables']['projects']['Row'];
type Task = Database['public']['Tables']['tasks']['Row'];
type Quote = Database['public']['Tables']['quotes']['Row'];
type QuoteLineItem = Database['public']['Tables']['quote_line_items']['Row'];
type Invoice = Database['public']['Tables']['invoices']['Row'];
type Payment = Database['public']['Tables']['payments']['Row'];
type Landing = Database['public']['Tables']['landings']['Row'];
type LandingBlock = Database['public']['Tables']['landing_blocks']['Row'];
type WebsiteContent = Database['public']['Tables']['website_content']['Row'];
type TeamMember = Database['public']['Tables']['team_members']['Row'];
type ProjectTeamAssignment = Database['public']['Tables']['project_team_assignments']['Row'];
type Lead = Database['public']['Tables']['leads']['Row'];
type CashflowEntry = Database['public']['Tables']['cashflow_entries']['Row'];

// Clients
export async function getClients() {
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Client[];
}

export async function getClient(id: number) {
  const { data, error } = await supabase
    .from('clients')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Client;
}

const CLIENT_WRITE_KEYS = [
  'name',
  'type',
  'sector',
  'contact',
  'email',
  'phone',
  'locale',
  'address',
  'last_contact',
] as const;

function pickClientWrite(
  input: Database['public']['Tables']['clients']['Insert'] | Database['public']['Tables']['clients']['Update']
) {
  const payload: Record<string, unknown> = {};
  for (const key of CLIENT_WRITE_KEYS) {
    const value = input[key];
    if (value !== undefined) payload[key] = value;
  }
  if (payload.last_contact === null || payload.last_contact === '') {
    delete payload.last_contact;
  }
  return payload;
}

function missingClientColumn(error: { message?: string; code?: string } | null) {
  const message = String(error?.message || '');
  if (message.includes("'address'")) return 'address';
  if (message.includes("'locale'")) return 'locale';
  return null;
}

async function writeClientRow(
  payload: Record<string, unknown>,
  write: (body: Record<string, unknown>) => Promise<{ data: Client | null; error: { message?: string; code?: string } | null }>
) {
  let body = { ...payload };
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const { data, error } = await write(body);
    if (!error && data) return data;
    const missing = missingClientColumn(error);
    if (missing && missing in body) {
      delete body[missing];
      continue;
    }
    throw error || new Error('Failed to save client');
  }
  throw new Error('Failed to save client');
}

export async function createClient(client: Database['public']['Tables']['clients']['Insert']) {
  return writeClientRow(pickClientWrite(client), async (body) => {
    const { data, error } = await supabase.from('clients').insert(body).select().single();
    return { data: data as Client | null, error };
  });
}

export async function updateClient(
  id: number,
  updates: Database['public']['Tables']['clients']['Update']
) {
  return writeClientRow(
    { ...pickClientWrite(updates), updated_at: new Date().toISOString() },
    async (body) => {
      const { data, error } = await supabase.from('clients').update(body).eq('id', id).select().single();
      return { data: data as Client | null, error };
    }
  );
}

export async function deleteClient(id: number) {
  const { error } = await supabase.from('clients').delete().eq('id', id);
  if (error) throw error;
}

// Projects
export async function getProjects() {
  const { data, error } = await supabase
    .from('projects')
    .select('*, clients(name, contact, email, phone, address, locale)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getProject(id: number) {
  const { data, error } = await supabase
    .from('projects')
    .select('*, clients(*)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function createProject(project: Database['public']['Tables']['projects']['Insert']) {
  const { data, error } = await supabase
    .from('projects')
    .insert(project)
    .select()
    .single();

  if (error) throw error;
  return data as Project;
}

export async function updateProject(
  id: number,
  updates: Database['public']['Tables']['projects']['Update']
) {
  const { data, error } = await supabase
    .from('projects')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Project;
}

export async function deleteProject(id: number) {
  const { error } = await supabase.from('projects').delete().eq('id', id);
  if (error) throw error;
}

export async function getProjectFeedback(projectId: number) {
  const { data, error } = await supabase
    .from('project_feedback')
    .select('id, kind, body, author_name, author_email, created_at')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Tasks
export async function getTasks(projectId: number) {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('project_id', projectId)
    .order('task_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as Task[];
}

export async function getTask(id: number) {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Task;
}

export async function createTask(task: Database['public']['Tables']['tasks']['Insert']) {
  const { data, error } = await supabase
    .from('tasks')
    .insert(task)
    .select()
    .single();

  if (error) throw error;
  return data as Task;
}

export async function updateTask(
  id: number,
  updates: Database['public']['Tables']['tasks']['Update']
) {
  const { data, error } = await supabase
    .from('tasks')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Task;
}

export async function deleteTask(id: number) {
  const { error } = await supabase.from('tasks').delete().eq('id', id);
  if (error) throw error;
}

export async function updateTaskOrder(taskId: number, newOrder: number) {
  const { data, error } = await supabase
    .from('tasks')
    .update({ task_order: newOrder, updated_at: new Date().toISOString() })
    .eq('id', taskId)
    .select()
    .single();

  if (error) throw error;
  return data as Task;
}

// Quotes
export async function getQuotes() {
  const { data, error } = await supabase
    .from('quotes')
    .select('*, clients(name), projects(id, name, budget, status)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getQuotesByProject(projectId: number) {
  const { data, error } = await supabase
    .from('quotes')
    .select('id, quote_number, amount, status, valid_until, created_at, project_id, client_id')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Quote[];
}

export async function getQuote(id: string) {
  const { data, error } = await supabase
    .from('quotes')
    .select(
      '*, clients(id, name, contact, email, phone, address, locale, type, sector), projects(name, share_content), quote_line_items(*)'
    )
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function createQuote(quote: Database['public']['Tables']['quotes']['Insert']) {
  const { data, error } = await supabase
    .from('quotes')
    .insert(quote)
    .select()
    .single();

  if (error) throw error;
  return data as Quote;
}

export async function updateQuote(
  id: string,
  updates: Database['public']['Tables']['quotes']['Update']
) {
  const { data, error } = await supabase
    .from('quotes')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Quote;
}

export async function deleteQuote(id: string) {
  const { error } = await supabase.from('quotes').delete().eq('id', id);
  if (error) throw error;
}

// Quote Line Items
export async function getQuoteLineItems(quoteId: string) {
  const { data, error } = await supabase
    .from('quote_line_items')
    .select('*')
    .eq('quote_id', quoteId)
    .order('item_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data as QuoteLineItem[];
}

export async function getQuoteLineItem(id: number) {
  const { data, error } = await supabase
    .from('quote_line_items')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as QuoteLineItem;
}

export async function createQuoteLineItem(
  lineItem: Database['public']['Tables']['quote_line_items']['Insert']
) {
  const { data, error } = await supabase
    .from('quote_line_items')
    .insert(lineItem)
    .select()
    .single();

  if (error) throw error;
  return data as QuoteLineItem;
}

export async function updateQuoteLineItem(
  id: number,
  updates: Database['public']['Tables']['quote_line_items']['Update']
) {
  const { data, error } = await supabase
    .from('quote_line_items')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as QuoteLineItem;
}

export async function deleteQuoteLineItem(id: number) {
  const { error } = await supabase.from('quote_line_items').delete().eq('id', id);
  if (error) throw error;
}

export async function updateQuoteLineItemOrder(itemId: number, newOrder: number) {
  const { data, error } = await supabase
    .from('quote_line_items')
    .update({ item_order: newOrder, updated_at: new Date().toISOString() })
    .eq('id', itemId)
    .select()
    .single();

  if (error) throw error;
  return data as QuoteLineItem;
}

// Invoices
export async function getInvoices() {
  const { data, error } = await supabase
    .from('invoices')
    .select('*, clients(*), projects(name)')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function getInvoice(id: string) {
  const { data, error } = await supabase
    .from('invoices')
    .select('*, clients(*), projects(name)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function createInvoice(invoice: Database['public']['Tables']['invoices']['Insert']) {
  const { data, error } = await supabase
    .from('invoices')
    .insert(invoice)
    .select()
    .single();

  if (error) throw error;
  return data as Invoice;
}

export async function updateInvoice(
  id: string,
  updates: Database['public']['Tables']['invoices']['Update']
) {
  const { data, error } = await supabase
    .from('invoices')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Invoice;
}

export async function deleteInvoice(id: string) {
  const { error } = await supabase.from('invoices').delete().eq('id', id);
  if (error) throw error;
}

export async function getPayments() {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .order('paid_at', { ascending: false });
  if (error) throw error;
  return data as Payment[];
}

export async function createPayment(payment: Database['public']['Tables']['payments']['Insert']) {
  const { data, error } = await supabase.from('payments').insert(payment).select().single();
  if (error) throw error;
  return data as Payment;
}

// Landings
export async function getLandings() {
  const { data, error } = await supabase
    .from('landings')
    .select('*')
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data as Landing[];
}

export async function getLanding(id: number) {
  const { data, error } = await supabase
    .from('landings')
    .select('*, landing_blocks(*)')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
}

export async function getLandingByUrl(url: string) {
  const { data, error } = await supabase
    .from("landings")
    .select("*, landing_blocks(*)")
    .eq("url", url)
    .single();

  if (error) throw error;
  return data;
}

export async function createLanding(landing: Database['public']['Tables']['landings']['Insert']) {
  const { data, error } = await supabase
    .from('landings')
    .insert(landing)
    .select()
    .single();

  if (error) throw error;
  return data as Landing;
}

export async function updateLanding(
  id: number,
  updates: Database['public']['Tables']['landings']['Update']
) {
  const { data, error } = await supabase
    .from('landings')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Landing;
}

/** Deletes a landing and its blocks (blocks first, in case the FK has no ON DELETE CASCADE). */
export async function deleteLanding(id: number) {
  const { error: blocksError } = await supabase.from('landing_blocks').delete().eq('landing_id', id);
  if (blocksError) throw blocksError;
  const { error } = await supabase.from('landings').delete().eq('id', id);
  if (error) throw error;
}

/** Copies a landing and its blocks as a private draft with a new URL. */
export async function duplicateLanding(id: number, copySuffix = 'copy') {
  const source = (await getLanding(id)) as Landing & { landing_blocks?: LandingBlock[] };
  const stamp = String(Date.now()).slice(-4);
  const copy = await createLanding({
    name: `${source.name} (${copySuffix})`,
    url: `${source.url}-${copySuffix}-${stamp}`,
    campaign: source.campaign,
    status: 'Draft',
    is_public: false,
    blocks: source.blocks,
    seo_metadata: source.seo_metadata,
  });
  const blocks = [...(source.landing_blocks || [])].sort((a, b) => a.block_order - b.block_order);
  if (blocks.length) {
    const { error } = await supabase.from('landing_blocks').insert(
      blocks.map((block) => ({
        landing_id: copy.id,
        type: block.type,
        block_order: block.block_order,
        content: block.content,
      }))
    );
    if (error) {
      await deleteLanding(copy.id);
      throw error;
    }
  }
  return copy;
}

export async function incrementLandingViews(id: number, views: number) {
  const nextViews = Number.isFinite(views) ? views + 1 : 1;
  const { data, error } = await supabase
    .from("landings")
    .update({ views: nextViews, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data as Landing;
}

// Landing Blocks
export async function getLandingBlocks(landingId: number) {
  const { data, error } = await supabase
    .from('landing_blocks')
    .select('*')
    .eq('landing_id', landingId)
    .order('block_order', { ascending: true });

  if (error) throw error;
  return data as LandingBlock[];
}

export async function createLandingBlock(
  block: Database['public']['Tables']['landing_blocks']['Insert']
) {
  const { data, error } = await supabase
    .from('landing_blocks')
    .insert(block)
    .select()
    .single();

  if (error) throw error;
  return data as LandingBlock;
}

export async function updateLandingBlock(
  id: number,
  updates: Database['public']['Tables']['landing_blocks']['Update']
) {
  const { data, error } = await supabase
    .from('landing_blocks')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as LandingBlock;
}

export async function deleteLandingBlock(id: number) {
  const { error } = await supabase.from('landing_blocks').delete().eq('id', id);
  if (error) throw error;
}

// Website Content
export async function getWebsiteContent(section?: string) {
  let query = supabase.from('website_content').select('*');

  if (section) {
    query = query.eq('section', section).single();
  } else {
    query = query.order('section', { ascending: true });
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function updateWebsiteContent(
  section: string,
  content: Database['public']['Tables']['website_content']['Update']
) {
  const { data, error } = await supabase
    .from('website_content')
    .upsert(
      {
        section,
        ...content,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: 'section',
      }
    )
    .select()
    .single();

  if (error) throw error;
  return data as WebsiteContent;
}

// Team Members
export async function getTeamMembers(activeOnly = false) {
  let query = supabase
    .from('team_members')
    .select('*')
    .order('name', { ascending: true });

  if (activeOnly) {
    query = query.eq('is_active', true);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as TeamMember[];
}

export async function getTeamMember(id: number) {
  const { data, error } = await supabase
    .from('team_members')
    .select('*')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as TeamMember;
}

export async function createTeamMember(
  member: Database['public']['Tables']['team_members']['Insert']
) {
  const { data, error } = await supabase
    .from('team_members')
    .insert(member)
    .select()
    .single();

  if (error) throw error;
  return data as TeamMember;
}

export async function updateTeamMember(
  id: number,
  updates: Database['public']['Tables']['team_members']['Update']
) {
  const { data, error } = await supabase
    .from('team_members')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as TeamMember;
}

export async function deleteTeamMember(id: number) {
  const { error } = await supabase.from('team_members').delete().eq('id', id);
  if (error) throw error;
}

// Project Team Assignments
export async function getProjectTeamAssignments(projectId: number) {
  const { data, error } = await supabase
    .from('project_team_assignments')
    .select('*, team_members(*)')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data;
}

export async function createProjectTeamAssignment(
  assignment: Database['public']['Tables']['project_team_assignments']['Insert']
) {
  const { data, error } = await supabase
    .from('project_team_assignments')
    .insert(assignment)
    .select('*, team_members(*)')
    .single();

  if (error) throw error;
  return data;
}

export async function updateProjectTeamAssignment(
  id: number,
  updates: Database['public']['Tables']['project_team_assignments']['Update']
) {
  const { data, error } = await supabase
    .from('project_team_assignments')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*, team_members(*)')
    .single();

  if (error) throw error;
  return data;
}

export async function deleteProjectTeamAssignment(id: number) {
  const { error } = await supabase
    .from('project_team_assignments')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// Leads
export async function getLeads() {
  const { data, error } = await supabase
    .from('leads')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data as Lead[];
}

export async function createLead(
  lead: Omit<Database['public']['Tables']['leads']['Insert'], 'product_interest'> & {
    product_interest?: string | null;
  }
) {
  const { data, error } = await supabase.from('leads').insert(lead).select().single();
  if (error) throw error;
  return data as Lead;
}

/**
 * Public website forms run as the anonymous role, which may INSERT leads but
 * not SELECT them (staff-only). Asking PostgREST to return the new row makes
 * Postgres apply the SELECT policy and reject the insert with 42501, so the
 * public path inserts without returning anything.
 */
export async function insertPublicLead(
  lead: Omit<Database['public']['Tables']['leads']['Insert'], 'product_interest'> & {
    product_interest?: string | null;
  }
) {
  const { error } = await supabase.from('leads').insert(lead);
  if (error) throw error;
}

export async function updateLead(
  id: number,
  updates: Database['public']['Tables']['leads']['Update']
) {
  const { data, error } = await supabase
    .from('leads')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as Lead;
}

export async function deleteLead(id: number) {
  const { error } = await supabase.from('leads').delete().eq('id', id);
  if (error) throw error;
}

// Cashflow
export async function getCashflowEntries() {
  const { data, error } = await supabase
    .from('cashflow_entries')
    .select('*, clients(name)')
    .order('entry_date', { ascending: false });

  if (error) throw error;
  return data;
}

export async function createCashflowEntry(
  entry: Database['public']['Tables']['cashflow_entries']['Insert']
) {
  const { data, error } = await supabase
    .from('cashflow_entries')
    .insert(entry)
    .select()
    .single();

  if (error) throw error;
  return data as CashflowEntry;
}

export async function updateCashflowEntry(
  id: number,
  updates: Database['public']['Tables']['cashflow_entries']['Update']
) {
  const { data, error } = await supabase
    .from('cashflow_entries')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as CashflowEntry;
}

export async function deleteCashflowEntry(id: number) {
  const { error } = await supabase.from('cashflow_entries').delete().eq('id', id);
  if (error) throw error;
}

